<script setup>
// Drzemki wpisu snu: godziny, opcjonalna jakość i notatka. Zapisują się razem
// z wpisem (pole `naps`), więc lista działa jak skład posiłku.
import TimeInput from '@/components/v2/time-input.vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    naps: { type: Array, default: () => [] },
    error: { type: String, default: null },
});
const emit = defineEmits(['update']);
const { t } = useI18n();

const minutes = (nap) => {
    if (!nap.startTime || !nap.endTime) return 0;
    const [sh, sm] = nap.startTime.split(':').map(Number);
    const [eh, em] = nap.endTime.split(':').map(Number);
    let diff = (eh * 60 + em) - (sh * 60 + sm);
    if (diff <= 0) diff += 24 * 60; // drzemka przez północ
    return diff;
};

const totalLabel = computed(() => {
    const total = props.naps.reduce((a, n) => a + minutes(n), 0);
    if (!total) return null;
    const h = Math.floor(total / 60);
    const m = total % 60;
    return h ? `${h}h ${String(m).padStart(2, '0')}` : `${m} min`;
});

const add = () => {
    emit('update', [...props.naps, { uuid: null, startTime: '14:00', endTime: '14:30', quality: '', notes: '' }]);
};

const change = (index, field, value) => {
    const next = [...props.naps];
    next[index] = { ...next[index], [field]: value };
    emit('update', next);
};

const remove = (index) => {
    emit('update', props.naps.filter((_, i) => i !== index));
};
</script>

<template>
    <div>
        <div class="flex items-baseline justify-between gap-3 mb-2">
            <span class="text-[9px] font-bold tracking-[0.18em] text-[#A29A8E]">{{ t('entry.naps.heading') }}</span>
            <span v-if="totalLabel" class="text-[11.5px] tabular-nums text-[#8A8378]">{{ t('entry.naps.total', { total: totalLabel }) }}</span>
        </div>

        <ul v-if="naps.length" class="flex flex-col gap-1.5 mb-2">
            <li v-for="(nap, i) in naps" :key="nap.uuid ?? i" class="flex items-center gap-2 group/nap">
                <TimeInput
                    :model-value="nap.startTime"
                    :aria-label="t('entry.naps.startOf', { n: i + 1 })"
                    class="w-[104px]"
                    input-class="!py-1 !px-2"
                    placeholder="--:--"
                    @update:model-value="v => change(i, 'startTime', v)"
                />
                <span class="text-[#CFC7BA] text-[12px]">–</span>
                <TimeInput
                    :model-value="nap.endTime"
                    :aria-label="t('entry.naps.endOf', { n: i + 1 })"
                    class="w-[104px]"
                    input-class="!py-1 !px-2"
                    placeholder="--:--"
                    @update:model-value="v => change(i, 'endTime', v)"
                />
                <input
                    :value="nap.quality"
                    type="number"
                    min="1"
                    max="100"
                    inputmode="numeric"
                    :placeholder="t('entry.naps.qualityPlaceholder')"
                    :aria-label="t('entry.naps.qualityOf', { n: i + 1 })"
                    class="v2-input v2-input--num !w-[74px] !py-1 !px-2"
                    @change="e => change(i, 'quality', e.target.value)"
                />
                <input
                    :value="nap.notes"
                    type="text"
                    :placeholder="t('entry.naps.notePlaceholder')"
                    :aria-label="t('entry.naps.noteOf', { n: i + 1 })"
                    class="v2-input !py-1 !px-2 flex-1 min-w-0"
                    @change="e => change(i, 'notes', e.target.value)"
                />
                <button
                    type="button"
                    :title="t('entry.naps.remove', { n: i + 1 })"
                    class="w-6 h-6 shrink-0 rounded flex items-center justify-center text-[#B8B0A3] opacity-100 lg:opacity-0 lg:group-hover/nap:opacity-100 focus-visible:opacity-100 hover:text-[#B5643C] hover:bg-[#F9F0EA] transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C0764B]"
                    @click="remove(i)"
                >
                    <i class="pi pi-times text-[10px]"></i>
                </button>
            </li>
        </ul>
        <p v-else class="text-[12.5px] text-[#A29A8E] mb-2">{{ t('entry.naps.empty') }}</p>

        <button
            type="button"
            class="text-[12.5px] font-semibold text-[#B5643C] hover:text-[#8E4B2C] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B] rounded"
            @click="add"
        >
            {{ t('entry.naps.add') }}
        </button>
        <p v-if="error" class="text-[11.5px] text-[#B5643C] mt-1">{{ error }}</p>
    </div>
</template>
